import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radar, ArrowRight, Terminal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const DEMO_LOGS = [
  "2026-07-13T08:57:22Z [warn] host=srv-web-02 outbound_to=45.155.204.7:4444",
  "2026-07-13T08:57:24Z [crit] host=wks-hr-05 edr.detect Emotet.Downloader",
  "2026-07-13T08:57:25Z [info] host=fw-edge-01 blocked_scan from=194.147.85.212",
  "2026-07-13T08:57:26Z [err]  host=srv-db-01 auth.fail root src=185.220.101.45",
  "2026-07-13T08:57:27Z [warn] host=srv-mail-01 smtp.relay from=91.219.29.11",
  "2026-07-13T08:57:29Z [info] host=srv-ad-01 tgs.request spn=svc_backup",
  "2026-07-13T08:57:31Z [crit] host=srv-ad-01 kerberoast.detect count=14",
  "2026-07-13T08:57:33Z [err]  host=srv-web-02 sqli.pattern uri=/login",
];

export default function Login() {
  const { login, loginMfa, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // MFA challenge state — non-null once the password step completes and MFA is required.
  const [mfaState, setMfaState] = useState(null);   // { challenge, mustSetup, userHint }
  const [mfaCode, setMfaCode] = useState("");
  const nav = useNavigate();

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setMfaState({ challenge: result.challenge, mustSetup: result.mustSetup, userHint: result.userHint });
        toast.info(result.mustSetup
          ? "Super-admin — set up your authenticator app right after this step."
          : "Enter your 6-digit authenticator code");
        return;
      }
      toast.success("Access granted");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginMfa(mfaState.challenge, mfaCode || "000000");
      toast.success("Access granted");
      nav(mfaState.mustSetup ? "/mfa/setup" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#060606] text-white">
      {/* Left: Form */}
      <div className="flex flex-col justify-between p-10 md:p-14">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <div className="relative w-9 h-9 border border-[#00F0FF] rounded-sm flex items-center justify-center">
            <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display font-black text-xl tracking-tight">FORENSIQ</div>
            <div className="text-[10px] font-mono text-[#8E9196] uppercase tracking-widest">Investigation-first cyber-defense</div>
            <div className="text-[9px] font-mono text-[#52525B] uppercase tracking-widest mt-1" data-testid="brand-signature-login">
              Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
            </div>
          </div>
        </Link>

        <form onSubmit={mfaState ? submitMfa : submit} className="max-w-sm w-full" data-testid="login-form">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">
            &gt; secure_terminal / auth{mfaState ? " / two-factor" : ""}
          </div>
          {!mfaState ? (
            <>
              <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-1">
                Every attack<br />has a signature.
              </h1>
              <p className="text-[#8E9196] mb-8">
                Sign in to investigate, attribute, and report.
              </p>

              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Email</label>
              <input
                data-testid="login-email"
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-4 bg-transparent border border-white/15 rounded-sm px-3 py-2.5 font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                placeholder="analyst@forensiq.io"
                required
              />

              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Password</label>
              <input
                data-testid="login-password"
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mb-6 bg-transparent border border-white/15 rounded-sm px-3 py-2.5 font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                placeholder="••••••••"
                required
              />

              <button
                data-testid="login-submit"
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors py-2.5 rounded-sm font-mono uppercase text-sm tracking-widest disabled:opacity-50"
              >
                {submitting ? "Authenticating…" : "Enter ForensiQ"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </>
          ) : (
            <>
              <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-1">
                Two-factor<br />verification.
              </h1>
              <p className="text-[#8E9196] mb-6">
                {mfaState.mustSetup
                  ? <>You&apos;ll set up your authenticator app right after this step. For now, hit <b>Continue</b> with any digits.</>
                  : <>Enter the 6-digit code from your authenticator app for <span className="text-white font-mono">{mfaState.userHint}</span>.
                      <br /><span className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">Lost your device? Paste a one-time recovery code instead.</span></>}
              </p>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Authenticator code</label>
              <input
                data-testid="login-mfa-code"
                type="text" inputMode="numeric" autoFocus autoComplete="one-time-code"
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\s+/g, ""))}
                className="w-full mb-6 bg-transparent border border-white/15 rounded-sm px-3 py-2.5 font-mono text-lg tracking-widest outline-none focus:border-[#B57BFF] focus:ring-1 focus:ring-[#B57BFF]"
                placeholder="000000"
                maxLength={8}
                required={!mfaState.mustSetup}
              />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setMfaState(null); setMfaCode(""); }}
                  className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
                  Back
                </button>
                <button
                  data-testid="login-mfa-submit"
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black transition-colors py-2.5 rounded-sm font-mono uppercase text-sm tracking-widest disabled:opacity-50"
                >
                  {submitting ? "Verifying…" : "Continue"}
                  <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
            </>
          )}
        </form>

        <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
          © 2026 · Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span> · ATT&amp;CK-aligned · Chain-of-custody
        </div>
      </div>

      {/* Right: Terminal art */}
      <div className="hidden md:block relative border-l border-white/10 overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative h-full p-8 flex flex-col scan-lines">
          <div className="flex items-center gap-2 text-[11px] font-mono text-[#8E9196] uppercase tracking-widest">
            <Terminal className="w-3.5 h-3.5" strokeWidth={1.5} />
            live_stream :: /var/log/sentinel/edge.log
          </div>
          <div className="mt-6 font-mono text-[12px] leading-relaxed space-y-1.5 flex-1">
            {DEMO_LOGS.map((l, i) => (
              <div key={i} className="opacity-90" style={{
                animation: `fadein 0.4s ease-out ${i * 0.1}s both`,
              }}>
                <span className="text-[#52525B]">{l.slice(0, 20)}</span>
                <span className={
                  l.includes("[crit]") ? "text-[#FF3B30]"
                  : l.includes("[err]") ? "text-[#FF3B30]"
                  : l.includes("[warn]") ? "text-[#FF9F0A]"
                  : "text-[#8E9196]"
                }>{" "}{l.slice(21, 27)}</span>
                <span className="text-white">{l.slice(27)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 text-[10px] font-mono text-[#52525B]">
            &gt; watching 8 sources · 2,431 events / min · integrity: ok
          </div>
        </div>

        <div className="absolute right-6 top-6">
          <div className="font-display font-black text-[110px] leading-none text-white/[0.04]">FQ</div>
        </div>
      </div>

      <style>{`@keyframes fadein { from { opacity: 0; transform: translateY(4px);} to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}
